import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { api } from '../../api.js';
import { useAuth } from '../../auth.jsx';
import Field from '../../components/Field.jsx';
import { checkEmail, checkRequired } from '../../utils/validate.js';
import Alert from '../../components/ui/Alert.jsx';
import ConfirmModal from '../../components/ui/ConfirmModal.jsx';
import Modal, { ModalActions } from '../../components/ui/Modal.jsx';
import { Facts, Loading, PageHeader, Pill, Section } from '../../components/ui/index.jsx';
import { fmtDate, fmtDateTime } from '../../utils/format.js';
import { ROLE_LABELS, RolePills, StatusPill, actorLabel, describeActivity, fullName } from './adminShared.jsx';

const WELCOME_NOTICE = {
  sent: 'User added. We’ve emailed them a link to set their password.',
  failed: 'User added, but the welcome email couldn’t be sent. Use “Send password reset” to try again.',
  skipped: 'User added. No email was sent: use “Send password reset” when they’re ready to sign in.',
};

/** Name and email. */
function EditUserModal({ user, onClose, onSaved }) {
  const [form, setForm] = useState({ first_name: user.first_name || '', last_name: user.last_name || '', email: user.email });
  const [errors, setErrors] = useState({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => { setForm({ ...form, [k]: e.target.value }); if (errors[k]) setErrors({ ...errors, [k]: undefined }); };

  const save = async () => {
    const found = Object.fromEntries(Object.entries({
      first_name: checkRequired(form.first_name, 'their first name', { max: 100 }),
      last_name: checkRequired(form.last_name, 'their last name', { max: 100 }),
      email: checkEmail(form.email, { label: 'their email address' }),
    }).filter(([, m]) => m));
    if (Object.keys(found).length) { setErrors(found); return; }
    setBusy(true); setError('');
    try {
      await api(`/admin/users/${user.id}`, { method: 'PATCH', body: form });
      onSaved();
    } catch (err) {
      setErrors(err.data?.fields || {});
      setError(err.data?.fields ? '' : err.message);
      setBusy(false);
    }
  };

  return (
    <Modal title="Edit details" onClose={onClose} onSubmit={save} busy={busy}
      footer={<ModalActions submitLabel="Save" busyLabel="Saving…" busy={busy} onCancel={onClose} />}>
      {error && <Alert tone="danger">{error}</Alert>}
      <div className="row-2">
        <Field label="First name" required maxLength={100} value={form.first_name} onChange={set('first_name')} error={errors.first_name} />
        <Field label="Last name" required maxLength={100} value={form.last_name} onChange={set('last_name')} error={errors.last_name} />
      </div>
      <Field label="Email address" type="email" autoCapitalize="none" required value={form.email} onChange={set('email')} error={errors.email}
        hint={!errors.email ? 'This is what they sign in with. Changing it doesn’t email the new address.' : undefined} />
    </Modal>
  );
}

/** Tick the roles they should have; saves only what changed. */
function RolesModal({ user, isYou, onClose, onSaved }) {
  const [roles, setRoles] = useState(user.roles);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const toggle = (r) => setRoles(roles.includes(r) ? roles.filter((x) => x !== r) : [...roles, r]);

  const save = async () => {
    setBusy(true); setError('');
    try {
      for (const r of Object.keys(ROLE_LABELS)) {
        const had = user.roles.includes(r);
        const has = roles.includes(r);
        if (had !== has) await api(`/admin/users/${user.id}/roles/${r}`, { method: has ? 'PUT' : 'DELETE' });
      }
      await onSaved();
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <Modal title="Change role" onClose={onClose} onSubmit={save} busy={busy} size="sm"
      footer={<ModalActions submitLabel="Save" busyLabel="Saving…" busy={busy} onCancel={onClose} />}>
      {error && <Alert tone="danger">{error}</Alert>}
      <fieldset className="fieldset">
        <legend className="sr-only">Roles</legend>
        {Object.entries(ROLE_LABELS).map(([key, label]) => {
          const lockSelf = isYou && key === 'admin';
          return (
            <label key={key} className="checkbox">
              <input type="checkbox" checked={roles.includes(key)} disabled={lockSelf} onChange={() => toggle(key)} />
              <span>
                <strong>{label}</strong>{key === 'admin' ? ' — full access to this admin area' : ' — uses the portal to protect their events'}
                {lockSelf && <span className="muted small"> (you can’t remove your own)</span>}
              </span>
            </label>
          );
        })}
      </fieldset>
      {!roles.length && <Alert tone="warn">With no role, this person can’t use SummitShield at all.</Alert>}
    </Modal>
  );
}

function PasswordResetModal({ user, isYou, onClose, onDone }) {
  const [signOut, setSignOut] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const send = async () => {
    setBusy(true); setError('');
    try {
      const res = await api(`/admin/users/${user.id}/password-reset`, { method: 'POST', body: { sign_out: signOut } });
      onDone(`Code sent to ${user.email}. It works for 15 minutes.${res.signed_out ? ' They’ve been signed out everywhere.' : ''}`);
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <Modal title="Send a password reset" onClose={onClose} onSubmit={send} busy={busy} size="sm"
      footer={<ModalActions submitLabel="Send code" busyLabel="Sending…" busy={busy} onCancel={onClose} />}>
      {error && <Alert tone="danger">{error}</Alert>}
      <p className="modal-text">We&rsquo;ll email <strong>{user.email}</strong> a 6-digit code to set a new password. You never see or choose their password.</p>
      {!isYou && (
        <label className="checkbox">
          <input type="checkbox" checked={signOut} onChange={(e) => setSignOut(e.target.checked)} />
          <span>Also sign them out everywhere now</span>
        </label>
      )}
    </Modal>
  );
}

function BlockModal({ user, onClose, onDone }) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const block = async () => {
    setBusy(true); setError('');
    try {
      await api(`/admin/users/${user.id}/block`, { method: 'POST', body: { reason } });
      await onDone();
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <Modal title={`Block ${fullName(user)}?`} onClose={onClose} onSubmit={block} busy={busy} size="sm" tone="danger"
      footer={<ModalActions submitLabel="Block" busyLabel="Blocking…" busy={busy} onCancel={onClose} danger />}>
      {error && <Alert tone="danger">{error}</Alert>}
      <p className="modal-text">They&rsquo;re signed out everywhere straight away and can&rsquo;t sign in or reset their password until you unblock them. Nothing is deleted.</p>
      <Field label="Reason (only admins see this)" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} />
    </Modal>
  );
}

/** Let a company out of test mode by hand (with a reason), or put it back. */
function TestModeModal({ organisation, onClose, onDone }) {
  const lifting = organisation.test_mode;
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [reasonError, setReasonError] = useState('');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (lifting && !reason.trim()) { setReasonError('Say why, for the record — e.g. “Confirmed by phone with the organiser.”'); return; }
    setBusy(true); setError('');
    try {
      await api(`/admin/organisations/${organisation.id}/test-mode`, { method: 'POST', body: { lifted: lifting, reason } });
      await onDone(lifting ? `${organisation.name} is out of test mode.` : `${organisation.name} is back in test mode.`);
    } catch (err) {
      if (err.data?.fields?.reason) setReasonError(err.data.fields.reason); else setError(err.message);
      setBusy(false);
    }
  };

  return (
    <Modal title={lifting ? `Take ${organisation.name} out of test mode?` : `Put ${organisation.name} back in test mode?`}
      onClose={onClose} onSubmit={save} busy={busy} size="sm"
      footer={<ModalActions submitLabel={lifting ? 'Take out of test mode' : 'Put back in test mode'} busy={busy} onCancel={onClose} />}>
      {error && <Alert tone="danger">{error}</Alert>}
      <p className="modal-text">
        {lifting
          ? 'Their verify pages become public and their check addresses accept mail from anyone, without a verified domain. Only do this when you’ve confirmed who they are another way.'
          : 'Their verify pages stop being public and their check addresses only accept mail from their own domain, until they verify a domain.'}
      </p>
      {lifting && <Field label="Why" required hint={!reasonError ? 'Kept in the audit log.' : undefined} value={reason} error={reasonError}
        onChange={(e) => { setReason(e.target.value); setReasonError(''); }} maxLength={500} counter />}
    </Modal>
  );
}

export default function AdminUser() {
  const { userId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { refresh: refreshMe } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState(location.state?.created ? WELCOME_NOTICE[location.state.welcome] : '');
  const [modal, setModal] = useState(null);   // edit | roles | reset | block | unblock | delete | {testMode: org}

  const load = useCallback(async () => {
    try {
      setData(await api(`/admin/users/${userId}`));
      setError('');
    } catch (err) {
      setError(err.status === 404 ? 'This user doesn’t exist, or has been deleted.' : err.message);
    }
  }, [userId]);
  useEffect(() => { load(); }, [load]);

  const back = { to: '/admin/users', label: 'Users' };
  if (error) return <div className="page"><PageHeader back={back} title="User" /><Alert tone="danger">{error}</Alert></div>;
  if (!data) return <div className="page"><Loading /></div>;

  const { user, organisations, activity, is_you: isYou } = data;
  const blocked = user.status === 'blocked';
  const done = async (message) => { setModal(null); if (message) setNotice(message); await load(); };

  return (
    <div className="page">
      <PageHeader
        back={back}
        title={<>{fullName(user)} {isYou && <span className="muted small">(you)</span>}</>}
        meta={<><span className="mono">{user.email}</span><StatusPill status={user.status} /></>}
        actions={<button type="button" className="btn btn-secondary" onClick={() => setModal('edit')}>Edit details</button>}
      />
      {notice && <Alert tone="success">{notice}</Alert>}
      {blocked && (
        <Alert tone="warn" title="Blocked from signing in">
          Since {fmtDateTime(user.blocked_at)}{user.blocked_reason ? ` — “${user.blocked_reason}”` : ''}. They can&rsquo;t sign in or reset their password.
        </Alert>
      )}

      <div className="admin-grid">
        <div className="admin-col">
          <Section title="Account">
            <Facts items={[
              ['Created', fmtDate(user.created_at)],
              ['Email confirmed', user.email_verified_at ? fmtDate(user.email_verified_at) : 'Not yet'],
              ['Password', user.has_password ? `Set ${user.password_changed_at ? fmtDate(user.password_changed_at) : ''}` : 'Not set yet'],
              ['Last signed in', fmtDateTime(user.last_seen)],
              ['Signed in on', data.active_sessions === 1 ? '1 device' : `${data.active_sessions} devices`],
            ]} />
          </Section>

          <Section title="Role" actions={<button type="button" className="btn btn-secondary btn-small" onClick={() => setModal('roles')}>Change</button>}>
            <RolePills roles={user.roles} />
          </Section>

          <Section title="Companies">
            {organisations.length === 0 ? <p className="muted small">None.</p> : (
              <ul className="org-list">
                {organisations.map((o) => (
                  <li key={o.id} className="org-row">
                    <span>
                      <strong>{o.name}</strong> <span className="muted small">— {o.role}, {o.event_count} event{o.event_count === 1 ? '' : 's'}, {o.member_count} member{o.member_count === 1 ? '' : 's'}, {o.verified_domains} verified domain{o.verified_domains === 1 ? '' : 's'}</span>
                      {' '}{o.test_mode ? <Pill tone="warn">Test mode</Pill> : <Pill tone="good">{o.verified_domains ? 'Verified' : 'Lifted by admin'}</Pill>}
                    </span>
                    {(o.test_mode || !o.verified_domains) && (
                      <button type="button" className="btn btn-small btn-secondary" onClick={() => setModal({ testMode: o })}>
                        {o.test_mode ? 'Take out of test mode' : 'Put back in test mode'}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </div>

        <div className="admin-col">
          <Section title="Access">
            <div className="action-list">
              <div className="action-row">
                <div><strong>Reset password</strong><p className="muted small">Email them a code to set a new one.</p></div>
                <button type="button" className="btn btn-secondary btn-small" disabled={blocked} onClick={() => setModal('reset')}
                  title={blocked ? 'Unblock the account first' : undefined}>Send reset</button>
              </div>
              {!isYou && (
                <div className="action-row">
                  <div><strong>{blocked ? 'Unblock' : 'Block from signing in'}</strong><p className="muted small">{blocked ? 'Let them sign in again.' : 'Sign them out now; keep everything.'}</p></div>
                  {blocked
                    ? <button type="button" className="btn btn-secondary btn-small" onClick={() => setModal('unblock')}>Unblock</button>
                    : <button type="button" className="btn btn-danger-ghost btn-small" onClick={() => setModal('block')}>Block</button>}
                </div>
              )}
              {!isYou && (
                <div className="action-row">
                  <div><strong>Delete account</strong><p className="muted small">Permanent. The activity record is kept.</p></div>
                  <button type="button" className="btn btn-danger-ghost btn-small" onClick={() => setModal('delete')}>Delete</button>
                </div>
              )}
            </div>
          </Section>

          <Section title="Activity">
            {activity.length === 0 ? <p className="muted small">Nothing recorded yet.</p> : (
              <ul className="activity">
                {activity.map((a) => (
                  <li key={a.id}>
                    <span>{describeActivity(a)}</span>
                    <span className="muted small">{fmtDateTime(a.created_at)}{a.actor_email && a.actor_user_id !== user.id ? ` · by ${actorLabel(a)}` : ''}</span>
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </div>
      </div>

      {modal?.testMode && <TestModeModal organisation={modal.testMode} onClose={() => setModal(null)} onDone={done} />}
      {modal === 'edit' && <EditUserModal user={user} onClose={() => setModal(null)} onSaved={() => done('Saved.')} />}
      {modal === 'roles' && (
        <RolesModal user={user} isYou={isYou} onClose={() => setModal(null)}
          onSaved={async () => { await done('Role updated.'); if (isYou) await refreshMe(); }} />
      )}
      {modal === 'reset' && <PasswordResetModal user={user} isYou={isYou} onClose={() => setModal(null)} onDone={done} />}
      {modal === 'block' && <BlockModal user={user} onClose={() => setModal(null)} onDone={() => done('Blocked. They’ve been signed out everywhere.')} />}
      {modal === 'unblock' && (
        <ConfirmModal title={`Unblock ${fullName(user)}?`} confirmLabel="Unblock" onClose={() => setModal(null)}
          onConfirm={async () => { await api(`/admin/users/${user.id}/unblock`, { method: 'POST' }); await done('Unblocked. They can sign in again.'); }}>
          <p>They&rsquo;ll be able to sign in and reset their password again.</p>
        </ConfirmModal>
      )}
      {modal === 'delete' && (
        <ConfirmModal title={`Delete ${fullName(user)}?`} confirmLabel="Delete account" danger typeToConfirm={user.email}
          onClose={() => setModal(null)}
          onConfirm={async (typed) => {
            await api(`/admin/users/${user.id}`, { method: 'DELETE', body: { confirm_email: typed } });
            navigate('/admin/users', { replace: true });
          }}>
          <p>Their name, email and password are removed and they lose access to every organisation. This can&rsquo;t be undone. To stop someone temporarily, block them instead.</p>
          {data.sole_organisations.length > 0 && (
            <Alert tone="danger">
              They&rsquo;re the only member of {data.sole_organisations.map((o) => o.name).join(', ')}. Nobody will be able to manage
              {data.sole_organisations.length > 1 ? ' those organisations' : ' that organisation'} or its events afterwards.
            </Alert>
          )}
        </ConfirmModal>
      )}
    </div>
  );
}
